import { isPlatformBrowser } from "@angular/common";
import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { EncryptedRequest, EncryptedResponse } from "@shared/api-obfuscation";

@Injectable()
export class EncryptionService {
  private encryptionCore: any = null;

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {}

  private async loadEncryptionCore() {
    if (!this.encryptionCore) {
      const module = await import("../encryption-core");
      const { EncryptionCoreService } = module;
      const { KeyAssemblyService } = module;

      // Create service instances
      const keyAssembly = new KeyAssemblyService();
      this.encryptionCore = new EncryptionCoreService(keyAssembly);
    }
    return this.encryptionCore;
  }

  async encryptRequest(data: any): Promise<EncryptedRequest> {
    // In SSR environment, return non-encrypted data
    if (!isPlatformBrowser(this.platformId)) {
      return {
        data: JSON.stringify(data),
        encrypted: false,
        nonce: "",
        t: 0
      };
    }

    const core = await this.loadEncryptionCore();
    const timeBlock = core.getTimeBlock();
    const key = await core.deriveKey(timeBlock);

    const nonce =
      typeof crypto !== "undefined" && crypto.getRandomValues
        ? crypto.getRandomValues(new Uint8Array(12))
        : new Uint8Array(12).map((_, i) => Math.floor(Math.random() * 256));

    const plaintext = new TextEncoder().encode(JSON.stringify(data));

    const ciphertext = await core.aesGcmEncrypt(plaintext, key, nonce);

    return {
      data: this.arrayBufferToBase64(ciphertext),
      encrypted: true,
      nonce: this.arrayBufferToBase64(nonce),
      t: core.obfuscateTimeBlock(timeBlock)
    };
  }

  async decryptResponse(response: EncryptedResponse): Promise<any> {
    if (!response.encrypted) {
      return response;
    }

    // In SSR environment, return the response as-is since we can't decrypt
    if (!isPlatformBrowser(this.platformId)) {
      console.warn("[EncryptionService] Cannot decrypt in SSR environment, returning raw response");
      return response;
    }

    const core = await this.loadEncryptionCore();
    const timeBlocks = core.getTimeBlocksToTry(response.t);
    let lastError: any;

    for (const timeBlock of timeBlocks) {
      try {
        const key = await core.deriveKey(timeBlock);
        const ciphertext = this.base64ToArrayBuffer(response.data);
        const iv = this.base64ToArrayBuffer(response.nonce);
        const decrypted = await core.aesGcmDecrypt(ciphertext, key, iv);
        return JSON.parse(new TextDecoder().decode(decrypted));
      } catch (e) {
        lastError = e;
        continue;
      }
    }

    console.error("[EncryptionService] Failed to decrypt response after trying all time blocks", lastError);
    throw new Error("Failed to decrypt response");
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  private base64ToArrayBuffer(base64: string): ArrayBuffer {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
  }
}
